import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { AuthService, AuthResult } from "../services/authService";
import { ApiService } from "../services/apiService";
import { BiaFitUser } from "../types/user";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { logger } from "../utils/logger";

interface AuthContextType {
  user: BiaFitUser | null;
  loading: boolean;
  signIn: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<AuthResult>;
  signOut: () => Promise<AuthResult>;
  resetPassword: (email: string) => Promise<AuthResult>;
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<BiaFitUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(() => {
    ApiService.setLogoutCallback(() => {
      setUser(null);
    });

    const initializeAuth = async () => {
      try {
        setLoading(true);
        const user = await AuthService.initializeAuth();

        if (user && user.role === "admin" && user.is_active) {
          setUser(user);
        } else {
          setUser(null);
          // Only clear tokens if we got a user but they're not admin/active
          if (user && (user.role !== "admin" || !user.is_active)) {
            AuthService.signOut();
          }
        }
      } catch (error) {
        setAuthError("Failed to initialize authentication");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const signIn = async (
    email: string,
    password: string,
    rememberMe: boolean = false
  ): Promise<AuthResult> => {
    try {
      const result = await AuthService.signIn(email, password, rememberMe);
      if (result.success && result.user) {
        setUser(result.user);
        await refreshUser();
      }
      return result;
    } catch (error) {
      return {
        success: false,
        error: "An unexpected error occurred during sign in",
      };
    }
  };

  const signOut = async (): Promise<AuthResult> => {
    setLoading(true);
    try {
      const result = await AuthService.signOut();
      if (result.success) {
        setUser(null);
      }
      return result;
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string): Promise<AuthResult> => {
    return await AuthService.resetPassword(email);
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const updatedUser = await AuthService.getCurrentUser();
      if (
        updatedUser &&
        updatedUser.role === "admin" &&
        updatedUser.is_active
      ) {
        setUser(updatedUser);
      }
    } catch (error) {
      logger.error("Error refreshing user:", error);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signOut,
    resetPassword,
    refreshUser,
    isAuthenticated: !!user && user.role === "admin" && user.is_active,
  };

  // Show loading screen if still loading
  if (loading) {
    return (
      <AuthContext.Provider value={value}>
        <div className="flex min-h-screen items-center justify-center bg-white dark:bg-navy-900">
          <div className="text-center">
            <LoadingSpinner bare className="mx-auto mb-4" />
            <h2 className="mb-2 text-lg font-semibold text-navy-700 dark:text-white">
              Loading BiaFit...
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Initializing authentication system
            </p>
            {authError && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                Error: {authError}
              </p>
            )}
          </div>
        </div>
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
